import axios from "axios";

const PRODUCT_GROUP_ENDPOINT =
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getProductGroupsByApp";
const PRODUCT_GROUP_CACHE_TTL_MS = 30 * 1000;

const cache = new Map();
const pendingRequests = new Map();
let requestGeneration = 0;

const normalizeApp = (app) => String(app || "").trim().toLowerCase();

const loadCached = async (cacheKey, body) => {
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.loadedAt < PRODUCT_GROUP_CACHE_TTL_MS) {
    return cached.data;
  }

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const generation = requestGeneration;
  const request = axios
    .post(PRODUCT_GROUP_ENDPOINT, body, { timeout: 15000 })
    .then(({ data }) => {
      // A response that started before a foreground recovery must not refill
      // the cache with data from the suspended browser session.
      if (generation === requestGeneration) {
        cache.set(cacheKey, { data, loadedAt: Date.now() });
      }
      return data;
    })
    .finally(() => {
      if (pendingRequests.get(cacheKey) === request) {
        pendingRequests.delete(cacheKey);
      }
    });

  pendingRequests.set(cacheKey, request);
  return request;
};

export async function getProductGroupByApp(app) {
  const normalizedApp = normalizeApp(app);
  if (!normalizedApp) throw new Error("app required");

  return loadCached(`summary:${normalizedApp}`, {
    app,
    includeProducts: false,
  });
}

export async function getProductGroupOptionProducts(app, groupId, optionId) {
  const normalizedApp = normalizeApp(app);
  const normalizedGroupId = String(groupId || "").trim();
  const normalizedOptionId = String(optionId || "").trim();
  if (!normalizedApp || !normalizedGroupId || !normalizedOptionId) {
    throw new Error("app, groupId and optionId are required");
  }

  const data = await loadCached(
    `option:${normalizedApp}:${normalizedGroupId}:${normalizedOptionId}`,
    {
      app,
      groupId: normalizedGroupId,
      optionId: normalizedOptionId,
      includeProducts: true,
    },
  );

  return data?.products || [];
}

export function invalidateProductGroupCache(app) {
  if (app) {
    const normalizedApp = normalizeApp(app);
    for (const key of cache.keys()) {
      if (key.includes(`:${normalizedApp}`)) cache.delete(key);
    }
    return;
  }

  cache.clear();
}

export function resetProductGroupRequests() {
  requestGeneration += 1;
  pendingRequests.clear();
}
